import React from 'react';
import { Chip, Tooltip } from '@mui/material';

export type StatusConfig = {
  label: string;
  color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  icon?: React.ReactElement;
  tooltip?: string;
};

export type StatusMapType = Record<string, StatusConfig>;

export type StatusChipProps = {
  status: string;
  statusMap: StatusMapType;
  size?: 'small' | 'medium';
  variant?: 'filled' | 'outlined';
};

const StatusChip: React.FC<StatusChipProps> = ({
  status,
  statusMap,
  size = 'small',
  variant = 'filled'
}) => {
  const config = statusMap[status] || {
    label: status,
    color: 'default'
  };

  // Create chip with or without icon based on if it exists
  const chip = config.icon ? (
    <Chip
      label={config.label}
      color={config.color}
      size={size}
      variant={variant}
      icon={config.icon}
    />
  ) : (
    <Chip
      label={config.label}
      color={config.color}
      size={size}
      variant={variant}
    />
  );

  if (config.tooltip) {
    return <Tooltip title={config.tooltip}>{chip}</Tooltip>;
  }

  return chip;
};

// Common status maps that can be reused
export const memberStatusMap: StatusMapType = {
  active: {
    label: 'Active',
    color: 'success',
    tooltip: 'Member has accepted the invitation and has access'
  },
  invited: {
    label: 'Invited',
    color: 'info',
    tooltip: 'Invitation sent, waiting for response'
  },
  suspended: {
    label: 'Suspended',
    color: 'error',
    tooltip: 'Member access has been temporarily suspended'
  },
  inactive: {
    label: 'Inactive',
    color: 'default',
    tooltip: 'Member no longer has access'
  }
};

export const subscriptionStatusMap: StatusMapType = {
  active: {
    label: 'Active',
    color: 'success'
  },
  trialing: {
    label: 'Trial',
    color: 'info'
  },
  past_due: {
    label: 'Past Due',
    color: 'warning'
  },
  unpaid: {
    label: 'Unpaid',
    color: 'error'
  },
  canceled: {
    label: 'Canceled',
    color: 'default'
  },
  incomplete: {
    label: 'Incomplete',
    color: 'warning'
  },
  expired: {
    label: 'Expired',
    color: 'default'
  }
};

/**
 * Get effective subscription status for display based on availability and status
 * @param status The subscription billing status
 * @param availability The subscription availability (active/inactive)
 * @returns The effective status to display
 */
export function getSubscriptionDisplayStatus(status: string, availability?: string): string {
  // If availability is inactive, the subscription cannot be used - show as "expired"
  // This covers all cases: canceled subscriptions past their period, active subscriptions
  // with autoRenew=false that are past their period, incomplete, unpaid, etc.
  if (availability === 'inactive') {
    return 'expired';
  }

  // Otherwise return the original status
  return status;
}

export default StatusChip;